import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Upload, Trash2, Sparkles, Music, Loader2, Tag, Gift } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { useAuth } from "../contexts/AuthContext";
import { getPricing } from "../lib/pricing";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const GENRES = ["Hip-Hop", "Trap", "Emotional Trap", "R&B", "Pop", "Lo-Fi", "Drill", "Afrobeat", "EDM", "Soul", "Gospel"];
const ART_STYLES = [
  { value: "realistic", label: "Realistic", hint: "Photoreal & cinematic" },
  { value: "drawing", label: "Drawing", hint: "Hand-drawn illustration" },
  { value: "cartoon", label: "Cartoon", hint: "Bold comic-book style" },
  { value: "3d", label: "3D", hint: "3D rendered artwork" },
];

const resolveUrl = (url) => (url?.startsWith("/api/") ? `${BACKEND_URL}${url}` : url);

export default function Admin() {
  const { admin, loading, logout } = useAuth();
  const navigate = useNavigate();

  const [beats, setBeats] = useState([]);
  const [form, setForm] = useState({
    title: "",
    genres: ["Hip-Hop"],
    bpm: "",
    key: "",
    price: "29.99",
    art_style: "realistic",
    featured: false,
  });
  const [audioFile, setAudioFile] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [deliverableFile, setDeliverableFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [dealBeat, setDealBeat] = useState(null);
  const [dealDiscount, setDealDiscount] = useState(0);
  const [dealFree, setDealFree] = useState(false);
  const [dealSaving, setDealSaving] = useState(false);

  const authConfig = { withCredentials: true };

  const fetchBeats = useCallback(async () => {
    const { data } = await axios.get(`${BACKEND_URL}/api/beats`);
    setBeats(data);
  }, []);

  useEffect(() => {
    if (!loading && !admin) navigate("/admin/login");
  }, [loading, admin, navigate]);

  useEffect(() => {
    if (admin) fetchBeats();
  }, [admin, fetchBeats]);

  if (loading || !admin) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-24">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!audioFile) {
      toast.error("Please upload an audio file");
      return;
    }
    if (form.genres.length === 0) {
      toast.error("Please select at least one genre");
      return;
    }
    setSubmitting(true);
    const fd = new FormData();
    fd.append("title", form.title);
    fd.append("genres", form.genres.join(","));
    fd.append("bpm", form.bpm);
    fd.append("key", form.key);
    fd.append("price", form.price);
    fd.append("art_style", form.art_style);
    fd.append("featured", form.featured);
    fd.append("audio", audioFile);
    if (coverFile) fd.append("cover", coverFile);
    if (deliverableFile) fd.append("deliverable", deliverableFile);

    try {
      await axios.post(`${BACKEND_URL}/api/admin/beats`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(coverFile ? "Beat added!" : "Beat added with AI cover art!");
      setForm({ title: "", genres: ["Hip-Hop"], bpm: "", key: "", price: "29.99", art_style: "realistic", featured: false });
      setAudioFile(null);
      setCoverFile(null);
      setDeliverableFile(null);
      e.target.reset();
      fetchBeats();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add beat");
    } finally {
      setSubmitting(false);
    }
  };

  const toggleGenre = (g) => {
    setForm((f) => ({
      ...f,
      genres: f.genres.includes(g) ? f.genres.filter((x) => x !== g) : [...f.genres, g],
    }));
  };

  const handleDelete = async (id) => {
    setBusyId(id);
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/beats/${id}`, authConfig);
      toast.success("Beat deleted");
      fetchBeats();
    } catch {
      toast.error("Failed to delete");
    } finally {
      setBusyId(null);
    }
  };

  const handleRegenerate = async (id, style) => {
    setBusyId(id);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/beats/${id}/regenerate-cover`,
        style ? { art_style: style } : {},
        authConfig
      );
      toast.success("New cover art generated!");
      fetchBeats();
    } catch {
      toast.error("Failed to regenerate cover");
    } finally {
      setBusyId(null);
    }
  };

  const openDeal = (beat) => {
    setDealBeat(beat);
    setDealDiscount(beat.discount_percent || 0);
    setDealFree(!!beat.is_free);
  };

  const handleSaveDeal = async () => {
    setDealSaving(true);
    try {
      await axios.patch(
        `${BACKEND_URL}/api/admin/beats/${dealBeat.id}`,
        { discount_percent: Number(dealDiscount) || 0, is_free: dealFree },
        authConfig
      );
      toast.success("Deal updated!");
      setDealBeat(null);
      fetchBeats();
    } catch {
      toast.error("Failed to update deal");
    } finally {
      setDealSaving(false);
    }
  };

  return (
    <div className="pt-24 pb-12 px-6 lg:px-8" data-testid="admin-page">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-10">
          <div>
            <h1 className="font-heading text-3xl lg:text-4xl font-black">Beat Manager</h1>
            <p className="text-muted-foreground mt-1">{admin.email}</p>
          </div>
          <Button
            variant="outline"
            onClick={logout}
            className="rounded-full gap-2"
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-4 h-4" /> Logout
          </Button>
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          {/* Add Beat Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="lg:col-span-2 bg-card border border-border rounded-2xl p-6 h-fit"
          >
            <h2 className="font-heading text-xl font-bold mb-5 flex items-center gap-2">
              <Upload className="w-5 h-5 text-primary" /> Add New Beat
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Title</Label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Midnight Vibes"
                  className="rounded-xl bg-background"
                  data-testid="beat-title-input"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>
                  Genres{" "}
                  <span className="text-xs text-muted-foreground font-normal">
                    (select one or more)
                  </span>
                </Label>
                <div className="flex flex-wrap gap-2" data-testid="beat-genres-select">
                  {GENRES.map((g) => {
                    const active = form.genres.includes(g);
                    return (
                      <button
                        type="button"
                        key={g}
                        onClick={() => toggleGenre(g)}
                        className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                          active
                            ? "bg-primary text-white border-primary"
                            : "bg-background border-border hover:border-primary/50"
                        }`}
                        data-testid={`genre-toggle-${g.toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        {g}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>BPM</Label>
                  <Input
                    type="number"
                    value={form.bpm}
                    onChange={(e) => setForm({ ...form, bpm: e.target.value })}
                    placeholder="90"
                    className="rounded-xl bg-background"
                    data-testid="beat-bpm-input"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Key</Label>
                  <Input
                    value={form.key}
                    onChange={(e) => setForm({ ...form, key: e.target.value })}
                    placeholder="Am"
                    className="rounded-xl bg-background"
                    data-testid="beat-key-input"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Price ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                  className="rounded-xl bg-background"
                  data-testid="beat-price-input"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Audio File (mp3 / wav)</Label>
                <Input
                  type="file"
                  accept="audio/*"
                  onChange={(e) => setAudioFile(e.target.files[0])}
                  className="rounded-xl bg-background file:text-primary"
                  data-testid="beat-audio-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  Cover Image
                  <span className="text-xs text-muted-foreground font-normal">
                    (optional — AI generates one if empty)
                  </span>
                </Label>
                <Input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setCoverFile(e.target.files[0])}
                  className="rounded-xl bg-background file:text-primary"
                  data-testid="beat-cover-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  AI Cover Art Style
                  <span className="text-xs text-muted-foreground font-normal">
                    (used when no cover is uploaded)
                  </span>
                </Label>
                <div className="grid grid-cols-2 gap-2" data-testid="beat-art-style-select">
                  {ART_STYLES.map((s) => {
                    const active = form.art_style === s.value;
                    return (
                      <button
                        type="button"
                        key={s.value}
                        onClick={() => setForm({ ...form, art_style: s.value })}
                        className={`text-left px-3 py-2 rounded-xl border transition-colors ${
                          active
                            ? "bg-primary/15 border-primary"
                            : "bg-background border-border hover:border-primary/50"
                        }`}
                        data-testid={`art-style-${s.value}`}
                      >
                        <span className={`block text-sm font-semibold ${active ? "text-primary" : ""}`}>
                          {s.label}
                        </span>
                        <span className="block text-xs text-muted-foreground">{s.hint}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  Deliverable File
                  <span className="text-xs text-muted-foreground font-normal">
                    (optional — high-quality file buyers download; preview used if empty)
                  </span>
                </Label>
                <Input
                  type="file"
                  accept="audio/*,.zip,.wav,.mp3"
                  onChange={(e) => setDeliverableFile(e.target.files[0])}
                  className="rounded-xl bg-background file:text-primary"
                  data-testid="beat-deliverable-input"
                />
              </div>

              <div className="flex items-center justify-between rounded-xl bg-background border border-border p-3">
                <Label htmlFor="featured-switch">Featured on homepage</Label>
                <Switch
                  id="featured-switch"
                  checked={form.featured}
                  onCheckedChange={(v) => setForm({ ...form, featured: v })}
                  data-testid="beat-featured-switch"
                />
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full h-12 rounded-xl bg-primary hover:bg-primary/90 neon-glow font-semibold gap-2"
                data-testid="beat-submit-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {coverFile ? "Uploading..." : "Generating cover & uploading..."}
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" /> Add Beat
                  </>
                )}
              </Button>
            </form>
          </motion.div>

          {/* Beat List */}
          <div className="lg:col-span-3">
            <h2 className="font-heading text-xl font-bold mb-5">
              Your Beats <span className="text-muted-foreground font-normal">({beats.length})</span>
            </h2>
            {beats.length === 0 ? (
              <div className="text-center py-16 bg-card border border-border rounded-2xl">
                <Music className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">No beats yet. Add your first one!</p>
              </div>
            ) : (
              <div className="space-y-3">
                {beats.map((beat) => (
                  <div
                    key={beat.id}
                    className="flex items-center gap-4 bg-card border border-border rounded-2xl p-4"
                    data-testid={`admin-beat-${beat.id}`}
                  >
                    <img
                      src={resolveUrl(beat.cover_url)}
                      alt={beat.title}
                      className="w-16 h-16 rounded-xl object-cover flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-bold truncate">{beat.title}</h3>
                        {beat.featured && (
                          <Badge className="bg-primary/20 text-primary text-xs">Featured</Badge>
                        )}
                        {beat.is_free && (
                          <Badge className="bg-green-500/20 text-green-400 text-xs" data-testid={`admin-free-badge-${beat.id}`}>FREE</Badge>
                        )}
                        {!beat.is_free && beat.discount_percent > 0 && (
                          <Badge className="bg-primary text-white text-xs" data-testid={`admin-discount-badge-${beat.id}`}>-{beat.discount_percent}%</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                        <span>{(beat.genres?.length ? beat.genres : [beat.genre]).join(", ")}</span>
                        <span>•</span>
                        <span>{beat.bpm} BPM</span>
                        <span>•</span>
                        <span>{beat.key}</span>
                        <span>•</span>
                        {(() => {
                          const p = getPricing(beat);
                          if (p.isFree) return <span className="text-green-400 font-semibold">Free</span>;
                          if (p.hasDiscount)
                            return (
                              <span>
                                <span className="line-through mr-1">${p.original}</span>
                                <span className="text-primary font-semibold">${p.final}</span>
                              </span>
                            );
                          return <span className="text-primary font-semibold">${p.original}</span>;
                        })()}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => openDeal(beat)}
                        title="Set deal / free"
                        className="rounded-full"
                        data-testid={`deal-btn-${beat.id}`}
                      >
                        <Tag className="w-4 h-4" />
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="outline"
                            size="icon"
                            disabled={busyId === beat.id}
                            title="Regenerate AI cover (pick a style)"
                            className="rounded-full"
                            data-testid={`regenerate-cover-${beat.id}`}
                          >
                            {busyId === beat.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Sparkles className="w-4 h-4" />
                            )}
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-52 bg-popover border-border">
                          <DropdownMenuLabel>Regenerate cover as…</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          {ART_STYLES.map((s) => (
                            <DropdownMenuItem
                              key={s.value}
                              onClick={() => handleRegenerate(beat.id, s.value)}
                              className="cursor-pointer flex-col items-start gap-0"
                              data-testid={`regenerate-style-${beat.id}-${s.value}`}
                            >
                              <span className="font-medium">{s.label}</span>
                              <span className="text-xs text-muted-foreground">{s.hint}</span>
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => handleDelete(beat.id)}
                        disabled={busyId === beat.id}
                        title="Delete beat"
                        className="rounded-full hover:bg-destructive/20 hover:text-destructive"
                        data-testid={`delete-beat-${beat.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Deal Dialog */}
      <Dialog open={!!dealBeat} onOpenChange={(o) => !o && setDealBeat(null)}>
        <DialogContent className="max-w-md bg-background border-border" data-testid="deal-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <Tag className="w-5 h-5 text-primary" />
              Deal for "{dealBeat?.title}"
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5 mt-4">
            <div className="flex items-center justify-between rounded-xl bg-card border border-border p-4">
              <div className="flex items-center gap-2">
                <Gift className="w-4 h-4 text-green-400" />
                <Label htmlFor="deal-free">Free beat this month</Label>
              </div>
              <Switch
                id="deal-free"
                checked={dealFree}
                onCheckedChange={setDealFree}
                data-testid="deal-free-switch"
              />
            </div>

            <div className={`space-y-2 transition-opacity ${dealFree ? "opacity-40 pointer-events-none" : ""}`}>
              <Label>Discount percentage (0–95%)</Label>
              <Input
                type="number"
                min="0"
                max="95"
                value={dealDiscount}
                onChange={(e) => setDealDiscount(e.target.value)}
                className="rounded-xl bg-card"
                data-testid="deal-discount-input"
                disabled={dealFree}
              />
              <p className="text-xs text-muted-foreground">
                Applies to all license tiers. Set 0 to remove the discount.
              </p>
            </div>

            <Button
              onClick={handleSaveDeal}
              disabled={dealSaving}
              className="w-full rounded-xl bg-primary hover:bg-primary/90 neon-glow gap-2"
              data-testid="deal-save-btn"
            >
              {dealSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Save Deal
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
